import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Smartphone, WifiOff, Home, Star, CheckCircle, Share, PlusSquare, MoreHorizontal } from 'lucide-react'
import { usePWA } from './PWAInstall'
import { useVedettes, premiereVedetteIllustree } from '../hooks/useVedettes'
import { formatPrice, t as traduire } from '../utils/format'
import './AppPromo.css'

/**
 * Trois avantages, et chacun existe reellement dans le code.
 *
 * La liste precedente ouvrait sur « Alertes promos en temps réel ». Les
 * notifications push ont ete retirees du projet : la table
 * push_subscriptions est supprimee, aucune route ne les enregistre, rien
 * n'en envoie. C'etait donc une fonctionnalite annoncee sur la page
 * d'accueil et absente du produit.
 *
 * Ce qui reste est mesurable : l'icone sur l'ecran d'accueil vient du
 * manifeste PWA, la navigation hors ligne du service worker qui met en
 * cache produits et categories, les favoris de la table wishlists.
 */
const features = [
  { icon: Home,    text: { fr: 'Une icône sur votre écran d\'accueil', en: 'An icon on your home screen' } },
  { icon: WifiOff, text: { fr: 'Le catalogue reste lisible hors ligne', en: 'The catalog stays readable offline' } },
  { icon: Star,    text: { fr: 'Accès rapide à vos favoris',            en: 'Quick access to your favorites' } },
]

const iosSteps = [
  { icon: Share,        text: { fr: 'Appuyez sur "Partager"',          en: 'Tap the "Share" button'           } },
  { icon: PlusSquare,   text: { fr: '"Sur l\'écran d\'accueil"',       en: '"Add to Home Screen"'             } },
  { icon: CheckCircle,  text: { fr: 'Appuyez sur "Ajouter"',           en: 'Tap "Add"'                        } },
]

/* Instructions manuelles Chrome/Edge/Firefox */
const desktopSteps = [
  { icon: MoreHorizontal, text: { fr: 'Cliquez sur ⋮ (menu du navigateur)',          en: 'Click ⋮ (browser menu)'              } },
  { icon: PlusSquare,     text: { fr: 'Sélectionnez "Installer ABBUSINESS…"',        en: 'Select "Install ABBUSINESS…"'        } },
  { icon: CheckCircle,    text: { fr: 'Confirmez l\'installation',                   en: 'Confirm the installation'            } },
]

export default function AppPromo({ lang }) {
  const t = (obj) => obj[lang]
  const { installable, installed, isIOS, install } = usePWA()
  const { vedettes } = useVedettes()
  const vedette = premiereVedetteIllustree(vedettes)
  const [showPanel, setShowPanel] = useState(false)

  const handleInstall = () => {
    if (installed) return
    /* Prompt natif dispo (Android/Desktop Chrome/Edge) */
    if (installable && !isIOS) { install(); return }
    /* iOS ou pas encore de prompt → afficher instructions */
    setShowPanel(v => !v)
  }

  const panelSteps = isIOS ? iosSteps : desktopSteps

  /* Label du bouton */
  const btnLabel = installed
    ? (lang === 'fr' ? 'Application installée' : 'App installed')
    : (lang === 'fr' ? 'Installer l\'application' : 'Install the app')

  return (
    <section className="app-promo">
      <div className="ap-inner">
        {/* ── Visuel téléphone ── */}
        <div className="ap-visual reveal-left">
          <div className="ap-phone">
            <div className="phone-notch" />
            <div className="phone-screen">
              <div className="ps-header">
                <span className="ps-logo">AB</span>
                <span className="ps-badge">● Live</span>
              </div>
              {/*
                | La maquette montrait « Salon Prestige 5P, 4 200 000 GNF »
                | sur une photo Unsplash, et une notification « Flash sale :
                | -30% sur les canapés ». Ni le produit, ni le prix, ni la
                | promotion n'existaient. Elle affiche maintenant une vraie
                | fiche du catalogue, ou rien du tout.
              */}
              {vedette && (
                <Link to={`/produits/${vedette.slug}`} className="ps-product">
                  <img
                    src={vedette.primary_image}
                    alt={traduire(vedette.name, lang)}
                    className="ps-img"
                    loading="lazy"
                  />
                  <div className="ps-info">
                    <div className="ps-name">{traduire(vedette.name, lang)}</div>
                    <div className="ps-price">
                      {formatPrice(vedette.effective_price ?? vedette.price)}
                    </div>
                    <div className="ps-btn">
                      {lang === 'fr' ? 'Voir la fiche' : 'View product'}
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>
          <div className="ap-glow" />
        </div>

        {/* ── Contenu ── */}
        <div className="ap-content reveal-right">
          <span className="section-tag">
            <Smartphone size={14} />
            {lang === 'fr' ? 'Application mobile' : 'Mobile app'}
          </span>

          <h2 className="section-title">
            {lang === 'fr' ? 'ABBUSINESS dans' : 'ABBUSINESS in'} <br />
            <span>{lang === 'fr' ? 'votre poche' : 'your pocket'}</span>
          </h2>

          <p className="section-subtitle">
            {lang === 'fr'
              ? 'Installez notre application directement depuis votre navigateur. Disponible sur iOS, Android et ordinateur. Aucun téléchargement depuis un store requis.'
              : 'Install our app directly from your browser. Available on iOS, Android and desktop. No store download required.'}
          </p>

          <ul className="ap-features">
            {features.map(({ icon: Icon, text }, i) => (
              <li key={i} className="ap-feature">
                <div className="ap-feature-icon"><Icon size={16} /></div>
                <span>{t(text)}</span>
              </li>
            ))}
          </ul>

          <div className="ap-actions">
            <button
              className={`btn btn-primary ap-install-btn${installed ? ' ap-install-done' : ''}`}
              onClick={handleInstall}
              disabled={installed}
            >
              {installed
                ? <CheckCircle size={18} />
                : <Smartphone size={18} />}
              {btnLabel}
            </button>

            <div className="ap-platforms">
              <span className="ap-platform">iOS</span>
              <span className="ap-platform">Android</span>
              <span className="ap-platform">Desktop</span>
            </div>
          </div>

          {/* Instructions inline — iOS ou Desktop manuel */}
          {showPanel && !installed && (
            <div className="ap-ios-steps">
              <p className="ap-ios-title">
                {isIOS
                  ? (lang === 'fr' ? '3 étapes pour installer sur iPhone / iPad' : '3 steps to install on iPhone / iPad')
                  : (lang === 'fr' ? 'Comment installer depuis Chrome / Edge' : 'How to install from Chrome / Edge')}
              </p>
              <ol className="ap-ios-list">
                {panelSteps.map(({ icon: Icon, text }, i) => (
                  <li key={i} className="ap-ios-step">
                    <div className="ap-ios-num">{i + 1}</div>
                    <Icon size={16} className="ap-ios-icon" />
                    <span>{t(text)}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <p className="ap-note">
            {lang === 'fr'
              ? '✓ Gratuit · ✓ Aucun App Store · ✓ Toujours à jour'
              : '✓ Free · ✓ No App Store needed · ✓ Always up to date'}
          </p>
        </div>
      </div>
    </section>
  )
}
